<template>
  <aside
    v-show="visible"
    class="cc-banner"
    :class="{ expanded: showingSettings }"
    :data-state="open ? 'open' : null"
    role="dialog"
    :aria-label="trans.get('landing.cookie.dialog_label')"
  >
    <!-- Primary view: short notice + 3 actions. -->
    <div
      v-show="!showingSettings"
      class="cc-primary"
    >
      <div class="cc-head">
        <span
          class="cc-logo"
          aria-hidden="true"
        >
          <icon-cookie />
        </span>
        <h3>{{ trans.get('landing.cookie.primary_title') }}</h3>
      </div>
      <div class="cc-body">
        <p>
          {{ trans.get('landing.cookie.primary_body_prefix') }}
          <a href="/datenschutz">{{ trans.get('landing.cookie.primary_body_link') }}</a
          >{{ trans.get('landing.cookie.primary_body_suffix') }}
        </p>
      </div>
      <div class="cc-actions">
        <button
          type="button"
          class="cc-btn primary"
          @click="acceptAll"
        >
          {{ trans.get('landing.cookie.accept_all') }}
        </button>
        <button
          type="button"
          class="cc-btn reject"
          @click="rejectAll"
        >
          {{ trans.get('landing.cookie.reject_all') }}
        </button>
        <button
          type="button"
          class="cc-btn tertiary"
          @click="openSettings()"
        >
          {{ trans.get('landing.cookie.open_settings') }}
        </button>
      </div>
    </div>

    <!-- Settings view: per-category toggles. -->
    <div
      v-show="showingSettings"
      class="cc-settings"
    >
      <div class="cc-head">
        <span
          class="cc-logo"
          aria-hidden="true"
        >
          <icon-cookie />
        </span>
        <h3>{{ trans.get('landing.cookie.settings_title') }}</h3>
        <button
          type="button"
          class="cc-back-btn"
          :aria-label="trans.get('landing.cookie.back_aria')"
          @click="closeSettings"
        >
          <icon-chevron-left
            :size="14"
            :stroke-width="2.5"
          />
        </button>
      </div>
      <div class="cc-body">
        <p class="cc-settings-intro">
          {{ trans.get('landing.cookie.settings_intro_prefix') }}
          <a href="/datenschutz">{{ trans.get('landing.cookie.settings_intro_link') }}</a
          >.
        </p>

        <ul class="cc-cats">
          <li
            v-for="cat in categories"
            :key="cat.key"
            class="cc-cat"
          >
            <div class="cc-cat-row">
              <div class="cc-cat-info">
                <div class="cc-cat-name">
                  {{ trans.get('landing.cookie.cats.' + cat.key + '.name') }}
                  <span
                    v-if="cat.locked"
                    class="cc-cat-locked"
                    >{{ trans.get('landing.cookie.always_on') }}</span
                  >
                </div>
                <div class="cc-cat-desc">
                  {{ trans.get('landing.cookie.cats.' + cat.key + '.desc') }}
                </div>
                <button
                  v-if="cat.services && cat.services.length"
                  type="button"
                  class="cc-cat-expand"
                  :aria-expanded="expandedCats[cat.key] ? 'true' : 'false'"
                  @click="toggleExpand(cat.key)"
                >
                  {{ trans.get('landing.cookie.details') }}
                  <icon-chevron-down
                    :size="10"
                    :stroke-width="2.5"
                  />
                </button>
                <div
                  v-if="cat.services && cat.services.length"
                  class="cc-cat-detail"
                  :class="{ open: expandedCats[cat.key] }"
                >
                  <div
                    v-for="svc in cat.services"
                    :key="svc.name"
                    class="svc"
                  >
                    <span class="nm">{{ svc.name }}</span>
                    <span class="dur">{{ svc.duration }}</span>
                  </div>
                </div>
              </div>
              <label
                class="cc-switch"
                :class="{ locked: cat.locked }"
                :aria-label="trans.get('landing.cookie.cats.' + cat.key + '.name')"
              >
                <input
                  type="checkbox"
                  role="switch"
                  :checked="cat.locked ? true : prefs[cat.key]"
                  :disabled="cat.locked"
                  @change="prefs[cat.key] = $event.target.checked"
                />
                <span class="track"></span>
                <span class="thumb"></span>
              </label>
            </div>
          </li>
        </ul>
      </div>
      <div class="cc-actions">
        <button
          type="button"
          class="cc-btn cc-save"
          @click="savePrefs"
        >
          {{ trans.get('landing.cookie.save_prefs') }}
        </button>
        <button
          type="button"
          class="cc-btn primary"
          @click="acceptAll"
        >
          {{ trans.get('landing.cookie.accept_all') }}
        </button>
        <button
          type="button"
          class="cc-btn reject"
          @click="rejectAll"
        >
          {{ trans.get('landing.cookie.reject_all') }}
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
// Mirrors the consent mechanism in viluu-site/index.html (search "cc-banner").
// Storage: localStorage as source of truth, mirrored into a cookie so server-side
// code can read the choice on the next request.
const HIDE_TRANSITION_MS = 320;

const TOGGLEABLE_CATEGORIES = ['functional', 'statistics', 'marketing'];

import {
  VILUU_CONSENT_COOKIE_MAX_AGE_SECONDS as COOKIE_MAX_AGE_SECONDS,
  VILUU_CONSENT_STORAGE_KEY as STORAGE_KEY,
  VILUU_CONSENT_STORAGE_VERSION as STORAGE_VERSION,
  viluuHasNonEssentialConsent,
} from '../../utils/consent';
import IconCookie from './icons/IconCookie.vue';
import IconChevronLeft from './icons/IconChevronLeft.vue';
import IconChevronDown from './icons/IconChevronDown.vue';

export default {
  name: 'CookieBanner',
  components: { IconCookie, IconChevronLeft, IconChevronDown },
  data() {
    return {
      // `visible` controls DOM presence; `open` drives the slide-in animation.
      // Two flags so we can play the leave transition before unmounting the node.
      visible: false,
      open: false,
      showingSettings: false,
      prefs: { functional: false, statistics: false, marketing: false },
      expandedCats: { functional: false, statistics: false, marketing: false },
      hideTimer: null,
      categories: [
        { key: 'necessary', locked: true, services: [] },
        {
          key: 'functional',
          locked: false,
          services: [
            { name: 'Registrierungs-Draft', duration: '30 Tage' },
            { name: 'Spracheinstellung', duration: '12 Monate' },
          ],
        },
        {
          key: 'statistics',
          locked: false,
          services: [{ name: 'Google Analytics', duration: '14 Monate' }],
        },
        {
          key: 'marketing',
          locked: false,
          services: [
            { name: 'Meta Pixel', duration: '90 Tage' },
            { name: 'TikTok Pixel', duration: '90 Tage' },
            { name: 'Google Ads', duration: '90 Tage' },
          ],
        },
      ],
    };
  },
  mounted() {
    this.$eventHub.$on('viluu-cookie:reopen', this.reopen);

    const stored = this.loadConsent();
    if (stored) {
      this.applyConsent(stored);
      return;
    }
    this.show();
  },
  beforeDestroy() {
    this.$eventHub.$off('viluu-cookie:reopen', this.reopen);
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
  },
  methods: {
    loadConsent() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          return null;
        }
        const parsed = JSON.parse(raw);
        return parsed && parsed.version === STORAGE_VERSION ? parsed : null;
      } catch (e) {
        return null;
      }
    },
    persistConsent(prefs) {
      const record = {
        necessary: true,
        functional: !!prefs.functional,
        statistics: !!prefs.statistics,
        marketing: !!prefs.marketing,
        version: STORAGE_VERSION,
        timestamp: new Date().toISOString(),
      };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
      } catch (e) {
        /* quota / private mode */
      }
      document.cookie =
        STORAGE_KEY +
        '=' +
        encodeURIComponent(JSON.stringify(record)) +
        '; path=/; max-age=' +
        COOKIE_MAX_AGE_SECONDS +
        '; SameSite=Lax';

      // Mirror into the legacy `laravel_cookie_consent` cookie that
      // viluu/layouts/_header.blade.php gates pixel loading on. The header
      // checks for existence only, so we write it whenever the user accepted
      // any non-essential category and skip it on reject-all. Without this
      // mirror, fbq/gtag/appsflyer/snap never fire on the v2 landing.
      if (viluuHasNonEssentialConsent(record)) {
        document.cookie =
          'laravel_cookie_consent=1; path=/; max-age=' + COOKIE_MAX_AGE_SECONDS + '; SameSite=Lax';
      }

      this.applyConsent(record);
      return record;
    },
    applyConsent(record) {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          ad_storage: record.marketing ? 'granted' : 'denied',
          ad_user_data: record.marketing ? 'granted' : 'denied',
          ad_personalization: record.marketing ? 'granted' : 'denied',
          analytics_storage: record.statistics ? 'granted' : 'denied',
        });
      }
      window.dispatchEvent(new CustomEvent('viluu:consent', { detail: record }));
    },
    show() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
      this.visible = true;
      this.$nextTick(() =>
        requestAnimationFrame(() => {
          this.open = true;
        })
      );
    },
    hide() {
      this.open = false;
      this.hideTimer = setTimeout(() => {
        this.visible = false;
        this.showingSettings = false;
        this.hideTimer = null;
      }, HIDE_TRANSITION_MS);
    },
    openSettings(prefill) {
      const source = prefill || this.loadConsent() || {};
      TOGGLEABLE_CATEGORIES.forEach((key) => {
        this.prefs[key] = !!source[key];
      });
      Object.keys(this.expandedCats).forEach((k) => {
        this.expandedCats[k] = false;
      });
      this.showingSettings = true;
    },
    closeSettings() {
      this.showingSettings = false;
    },
    toggleExpand(key) {
      this.expandedCats[key] = !this.expandedCats[key];
    },
    acceptAll() {
      this.decide({ functional: true, statistics: true, marketing: true });
    },
    rejectAll() {
      this.decide({ functional: false, statistics: false, marketing: false });
    },
    savePrefs() {
      this.decide({
        functional: this.prefs.functional,
        statistics: this.prefs.statistics,
        marketing: this.prefs.marketing,
      });
    },
    decide(prefs) {
      this.persistConsent(prefs);
      this.hide();
    },
    reopen() {
      this.openSettings(this.loadConsent() || {});
      this.show();
    },
  },
};
</script>

<style scoped lang="scss">
.cc-banner {
  position: fixed;
  bottom: 24px;
  left: 24px;
  right: 24px;
  max-width: 440px;
  z-index: 8500;
  background: #fffaf7;
  border-radius: 24px;
  border: 1px solid var(--line);
  box-shadow: 0 30px 60px -20px rgba(20, 5, 15, 0.35), 0 8px 20px -8px rgba(198, 62, 124, 0.2);
  overflow: hidden;
  transform: translateY(20px);
  opacity: 0;
  transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
  color: var(--ink);
  font-family: var(--sans);

  &[data-state='open'] {
    transform: translateY(0);
    opacity: 1;
  }

  &.expanded {
    max-width: 540px;
    // Fix the card height on desktop so expanding the category details scrolls
    // the list instead of growing the panel. The viewport term keeps it
    // on-screen on short windows; mobile overrides this below.
    height: min(710px, calc(100vh - 48px));
    display: flex;
    flex-direction: column;
  }

  // The settings view fills the banner as a column so its header and action
  // buttons stay pinned while the category list scrolls. `min-height: 0` lets
  // the scrolling body shrink below its content height inside the flex parent.
  &.expanded .cc-settings {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  // The body is a column: the intro stays pinned at the top while only the
  // category list scrolls. `min-height: 0` lets the list shrink and scroll
  // inside the flex parent instead of overflowing the banner.
  &.expanded .cc-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    margin: 16px 0;
    padding-top: 0;
    padding-bottom: 16px;
  }

  &.expanded .cc-cats {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }
}

.cc-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 0;

  .cc-logo {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, var(--coral), var(--pink));
    color: #fff;
    flex-shrink: 0;
    box-shadow: 0 8px 16px -4px rgba(198, 62, 124, 0.5);
  }

  .cc-logo svg {
    width: 18px;
    height: 18px;
  }

  h3 {
    margin: 0;
    font-family: var(--display);
    font-size: 18px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.01em;
    color: var(--ink);
  }
}

.cc-back-btn {
  margin-left: auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f4ecea;
  color: var(--ink-dim);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s ease;

  &:hover {
    background: #ebe0dd;
    color: var(--ink);
  }
}

.cc-body {
  padding: 14px 24px 0;

  p {
    font-size: 13.5px;
    color: var(--ink-dim);
    margin: 0 0 16px;
    text-wrap: pretty;
  }

  a {
    color: var(--pink);
    font-weight: 600;
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.15s ease;
  }

  a:hover {
    border-bottom-color: var(--pink);
  }
}

.cc-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 0 24px 25px;

  .cc-btn {
    padding: 13px 16px;
    border: none;
    border-radius: 99px;
    font: inherit;
    font-weight: 700;
    font-size: 13.5px;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    line-height: normal;
  }

  .cc-btn.primary {
    background: linear-gradient(120deg, var(--pink), var(--coral));
    color: #fff;
    box-shadow: 0 12px 24px -8px rgba(198, 62, 124, 0.55);
  }
  .cc-btn.primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 30px -8px rgba(198, 62, 124, 0.7);
  }

  .cc-btn.reject {
    background: #1a0c14;
    color: #fff;
    box-shadow: 0 12px 24px -8px rgba(26, 12, 20, 0.4);
  }
  .cc-btn.reject:hover {
    transform: translateY(-1px);
    background: #2d1420;
  }

  .cc-btn.tertiary {
    grid-column: 1 / -1;
    background: transparent;
    color: var(--ink-dim);
    font-weight: 600;
    padding: 8px 16px;
  }
  .cc-btn.tertiary:hover {
    color: var(--ink);
    background: #f4ecea;
  }
}

// The intro stays pinned above the scrolling list, so it owns the gap down
// to it.
.cc-settings-intro {
  margin: 0 0 16px;
}

// Categories
.cc-cats {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cc-cat {
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px 16px;
  background: #fff;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgba(198, 62, 124, 0.3);
  }
}

.cc-cat-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
}

.cc-cat-info {
  flex: 1;
}

.cc-cat-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--display);
  font-weight: 700;
  font-size: 14.5px;
  line-height: normal;
  color: var(--ink);
  letter-spacing: -0.01em;
}

.cc-cat-locked {
  font-size: 10px;
  color: var(--ink-mute);
  background: #f4ecea;
  padding: 2px 8px;
  border-radius: 99px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-family: var(--sans);
}

.cc-cat-desc {
  font-size: 12.5px;
  color: var(--ink-dim);
  line-height: 1.3;
  margin-top: 4px;
}

.cc-cat-expand {
  margin-top: 8px;
  font-size: 12px;
  line-height: normal;
  color: var(--pink);
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg {
    transition: transform 0.2s ease;
  }
  &[aria-expanded='true'] svg {
    transform: rotate(180deg);
  }
}

.cc-cat-detail {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--line);
  font-size: 11.5px;
  color: var(--ink-dim);
  line-height: normal;
  display: none;

  &.open {
    display: block;
  }

  .svc {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    gap: 12px;

    .nm {
      color: var(--ink);
      font-weight: 600;
    }
    .dur {
      color: var(--ink-mute);
      white-space: nowrap;
      font-size: 11px;
    }
  }
}

// Switch
.cc-switch {
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .track {
    position: absolute;
    inset: 0;
    background: #e6dad6;
    border-radius: 99px;
    transition: background 0.25s ease;
  }

  .thumb {
    position: absolute;
    left: 2px;
    top: 2px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 4px rgba(20, 5, 15, 0.18);
    transition: left 0.25s cubic-bezier(0.16, 1.4, 0.5, 1);
  }

  input:checked + .track {
    background: linear-gradient(135deg, var(--coral), var(--pink));
  }
  input:checked ~ .thumb {
    left: 22px;
  }
  input:focus-visible + .track {
    box-shadow: 0 0 0 3px rgba(198, 62, 124, 0.3);
  }

  &.locked {
    cursor: not-allowed;
    opacity: 0.7;

    .track {
      background: linear-gradient(135deg, var(--coral), var(--pink));
    }
    .thumb {
      left: 22px;
    }
  }
}

// Settings-view footer actions: 3-action layout.
.cc-banner.expanded .cc-actions {
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  .cc-save {
    grid-column: 1 / -1;
    background: linear-gradient(120deg, var(--pink), var(--coral));
    color: #fff;
    box-shadow: 0 12px 24px -8px rgba(198, 62, 124, 0.55);

    &:hover {
      transform: translateY(-1px);
    }
  }
}

@media (max-width: 520px) {
  .cc-banner {
    left: 0;
    right: 0;
    bottom: 0;
    max-width: 100%;
    border-radius: 24px 24px 0 0;
    border-bottom: none;

    &::before {
      content: '';
      position: absolute;
      top: 8px;
      left: 50%;
      transform: translateX(-50%);
      width: 36px;
      height: 4px;
      border-radius: 99px;
      background: #e6dad6;
    }

    // Bottom sheet keeps the original behaviour: grow with content up to a
    // viewport cap (overriding the desktop fixed height above).
    &.expanded {
      height: auto;
      max-height: 90vh;
    }
  }

  .cc-head {
    padding-top: 24px;
  }

  .cc-actions {
    grid-template-columns: 1fr;
    padding-bottom: max(22px, env(safe-area-inset-bottom));
  }
}
</style>
